import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Send, User, Bot, UserCog, Sparkles, CheckCheck, ArrowLeft, Info } from "lucide-react";

const STATE_STYLE = {
  ai_active: "bg-indigo-100 text-indigo-800 border-indigo-200",
  human_active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  waiting: "bg-amber-100 text-amber-800 border-amber-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const SCORE_STYLE = {
  HOT: "bg-rose-100 text-rose-800 border-rose-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-amber-100 text-amber-800 border-amber-200",
  LOW: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Inbox() {
  const { convId } = useParams();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const bottomRef = useRef(null);

  const loadList = async () => setConvs((await api.get("/conversations")).data);
  useEffect(() => { loadList(); const t = setInterval(loadList, 6000); return () => clearInterval(t); }, []);

  const openConv = async (id) => {
    nav(`/inbox/${id}`);
    const r = await api.get(`/conversations/${id}`);
    setActive(r.data);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };
  useEffect(() => { if (convId) openConv(convId); }, [convId]);

  const send = async () => {
    if (!msg.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.conversation.id}/send`, { body: msg });
      setMsg(""); await openConv(active.conversation.id); loadList();
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  const simulate = async () => {
    if (!msg.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.conversation.id}/simulate-incoming`, { body: msg });
      setMsg(""); toast.info("Simulated customer reply (mock)"); await openConv(active.conversation.id); loadList();
    } catch (e) { toast.error("Simulate failed"); }
    finally { setSending(false); }
  };

  const takeover = async (to_state) => {
    await api.post(`/conversations/${active.conversation.id}/handover`, { to_state });
    toast.success(to_state === "human_active" ? "Human takeover active" : "AI resumed");
    await openConv(active.conversation.id); loadList();
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex bg-white" data-testid="inbox-page">
      {/* Conversation list */}
      <aside className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 lg:w-96 border-r border-slate-200`}>
        <div className="p-3 border-b border-slate-100">
          <div className="font-semibold text-slate-900">Inbox</div>
          <div className="text-xs text-slate-500">{convs.length} conversations</div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {convs.map((c) => (
            <button
              key={c.id}
              onClick={() => openConv(c.id)}
              data-testid={`conversation-item-${c.id}`}
              className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 ${active?.conversation?.id === c.id ? "bg-emerald-50/50" : ""}`}
            >
              <div className="flex items-start gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    {(c.contact?.name || "?").slice(0,2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm truncate">{c.contact?.name || c.contact?.phone}</div>
                    <div className="text-[10px] text-slate-400 shrink-0">{c.last_message_at && new Date(c.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={`text-[9px] ${STATE_STYLE[c.state]}`}>{c.state.replace("_"," ")}</Badge>
                    {c.unread > 0 && <Badge className="text-[9px] bg-emerald-600">{c.unread}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-1">{c.last_message_preview}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <section className={`${active ? "flex" : "hidden md:flex"} flex-col flex-1 chat-bg`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="h-14 px-4 border-b border-slate-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={()=>{setActive(null); nav('/inbox');}} data-testid="back-to-list-button"><ArrowLeft className="w-4 h-4"/></Button>
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{(active.contact?.name || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                <div>
                  <div className="text-sm font-semibold">{active.contact?.name}</div>
                  <div className="text-xs text-slate-500">{active.contact?.phone}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATE_STYLE[active.conversation.state]}>{active.conversation.state.replace("_"," ")}</Badge>
                {active.conversation.state !== "human_active" ? (
                  <Button size="sm" onClick={()=>takeover("human_active")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="human-takeover-button"><UserCog className="w-3 h-3 mr-1"/>Take over</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={()=>takeover("ai_active")} data-testid="resume-ai-button"><Sparkles className="w-3 h-3 mr-1"/>Resume AI</Button>
                )}
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={()=>setShowDetails(v=>!v)} data-testid="toggle-details-button"><Info className="w-4 h-4"/></Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2">
              {active.messages.map((m) => {
                const isOut = m.direction === "outbound";
                const bubble = m.sender === "ai" ? "bg-indigo-50 text-indigo-950 border-indigo-200"
                  : m.sender === "human" ? "bg-emerald-50 text-emerald-950 border-emerald-200"
                  : m.sender === "system" ? "bg-emerald-500 text-white border-emerald-600"
                  : "bg-white text-slate-800 border-slate-200";
                return (
                  <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl border text-sm shadow-sm ${bubble}`}>
                      {m.sender === "ai" && <div className="text-[10px] font-semibold text-indigo-700 flex items-center gap-1 mb-1"><Bot className="w-3 h-3"/>Aria AI</div>}
                      {m.sender === "human" && <div className="text-[10px] font-semibold text-emerald-700 flex items-center gap-1 mb-1"><User className="w-3 h-3"/>Sales team</div>}
                      <div className="whitespace-pre-wrap">{m.body}</div>
                      <div className={`text-[10px] mt-1 flex items-center gap-1 ${isOut ? (m.sender === "system" ? "text-white/80" : "text-slate-500") : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {isOut && m.status === "read" && <CheckCheck className="w-3 h-3 text-blue-500"/>}
                        {isOut && m.status === "delivered" && <CheckCheck className="w-3 h-3"/>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef}></div>
            </div>
            <div className="p-3 border-t border-slate-200 bg-white space-y-2">
              <div className="flex gap-2">
                <Input value={msg} onChange={(e)=>setMsg(e.target.value)} placeholder="Type a message…" onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()} data-testid="message-input"/>
                <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-message-button"><Send className="w-4 h-4"/></Button>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-slate-400">MOCK MODE: no real WhatsApp message is sent</div>
                <Button size="sm" variant="outline" onClick={simulate} disabled={sending} data-testid="simulate-customer-button">
                  <Sparkles className="w-3 h-3 mr-1"/>Simulate customer reply
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Details pane */}
      {active && (
        <aside className={`${showDetails ? "block" : "hidden"} lg:block w-full lg:w-80 border-l border-slate-200 bg-white overflow-y-auto scrollbar-thin absolute lg:relative right-0 top-14 lg:top-0 bottom-0 z-20`}>
          <div className="p-4 space-y-4">
            <div>
              <div className="text-xs uppercase text-slate-500 tracking-wide font-medium">Customer</div>
              <div className="mt-1 font-semibold text-slate-900">{active.contact?.name}</div>
              <div className="text-sm text-slate-600">{active.contact?.phone}</div>
              {active.contact?.email && <div className="text-sm text-slate-600">{active.contact.email}</div>}
              {active.contact?.location && <div className="text-sm text-slate-500 mt-1">📍 {active.contact.location}</div>}
            </div>
            {active.conversation.ai_summary && (
              <Card className="p-3 border-indigo-200 bg-indigo-50">
                <div className="text-[10px] uppercase text-indigo-700 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3"/>AI Summary</div>
                <div className="text-xs text-indigo-950 mt-1">{active.conversation.ai_summary}</div>
              </Card>
            )}
            {active.lead && (
              <div className="space-y-2">
                <div className="text-xs uppercase text-slate-500 tracking-wide font-medium">Lead</div>
                <div className="flex items-center gap-2">
                  <Badge className={SCORE_STYLE[active.lead.score]}>{active.lead.score}</Badge>
                  <Badge variant="outline">{active.lead.status}</Badge>
                </div>
                {active.lead.requirement && <Row k="Requirement" v={active.lead.requirement}/>}
                {active.lead.budget && <Row k="Budget" v={active.lead.budget}/>}
                {active.lead.location && <Row k="Location" v={active.lead.location}/>}
                {active.lead.timeline && <Row k="Timeline" v={active.lead.timeline}/>}
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="text-xs"><span className="text-slate-500">{k}: </span><span className="text-slate-900 font-medium">{v}</span></div>;
}
